import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Cart from '../Selection/Cart';
import { goToPage, searchInput, changeLanguage, changeCountry, changeConsumer } from '../../actions/environment';
import { addClass, removeClass, deleteCookie } from '../../utils/dom';
import { translate, prefix } from '../../utils/locale';
import s from './Navigation.scss';


const Navigation = ({ navigation, setup, location, searchInputText, pagePaths, productCache, checkout, loggedIn, ...props }) => {
	const { selection = {}, account = {} } = checkout;
	const { totals = {} } = selection;
	const menuItems = useRef(null);
	const [mobileMenuOpen, toggleMobileMenu] = useState(false);
	const [toggledMenuParent, toggleMenuParent] = useState('');
	const [showHoverClear, toggleHoverClear] = useState(false);
	const [cartOpen, toggleCart] = useState(false);
	const [menuHeight, calculateMenuHeight] = useState(0);
	const [tradeTitle, updateTradeTitle] = useState(setup.consumerType === 'private' ? translate(setup, 'consumerPrivate') : translate(setup, 'consumerTrade'));

	const fixBackgroundScroll = (fixed) => {
		if (fixed) {
			const $html = document.querySelector('html');
			addClass(document.body, ['u_headerIsFixed', 'u_pageIsScrollingUp']);

			const scrollY = window.pageYOffset;
			document.body.style.position = 'fixed';
			document.body.style.top = `-${scrollY < 34 ? 34 : scrollY}px`;
			document.body.style.overflow = 'hidden';
			addClass(document.body, 'u_navigationOpen');

			setTimeout(() => {
				addClass($html, ['u_headerIsFixed', 'u_pageIsScrollingUp']);
				removeClass(document.body, ['u_headerIsFixed', 'u_pageIsScrollingUp']);
			}, 500);
		} else {
			const scrollY = document.body.style.top;
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.overflow = '';
			window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
			removeClass(document.body, 'u_navigationOpen');
		}
	};

	const	menuClicked = () => {
		toggleMobileMenu(false);
		fixBackgroundScroll(false);
		calculateMenuHeight(0);
		toggleMenuParent('');
		if (searchInputText !== '') props.searchInput('');
	};

	const navigationClicked = () => {
		toggleHoverClear(true);
		setTimeout(() => {
			toggleHoverClear(false);
		}, 1000);
		menuClicked();
	};

	const navigationParentClicked = (e) => {
		const link = e.currentTarget.getAttribute('href');
		if (window.innerWidth <= 900 && mobileMenuOpen && link !== toggledMenuParent) {
			e.preventDefault();
			toggleMenuParent(link);
			return;
		}
		navigationClicked();
	};

	const mobileMenuHandler = (e) => {
		if (cartOpen) {
			// Close cart menu
			if (typeof e !== 'boolean') e.preventDefault();
			fixBackgroundScroll(false);
			calculateMenuHeight(0);
			toggleCart(false);
		} else if (typeof e !== 'boolean') {
			// Toggle mobile menu
			e.preventDefault();
			toggleMenuParent('');
			toggleMobileMenu((prevState) => {
				fixBackgroundScroll(!prevState);
				calculateMenuHeight(prevState ? 0 : window.innerHeight);
				return !prevState;
			});
		} else {
			// e determines menu
			fixBackgroundScroll(e);
			calculateMenuHeight(e ? 0 : window.innerHeight);
			toggleMobileMenu(e);
			toggleMenuParent('');
		}
	};

	const openCartHandler = (e) => {
		if (window.innerWidth <= 900 && !cartOpen) {
			e.preventDefault();
			if (mobileMenuOpen) {
				toggleMobileMenu(false);
				calculateMenuHeight(0);
				setTimeout(() => {
					toggleCart(true);
					calculateMenuHeight(window.innerHeight);
				}, 300);
			} else {
				toggleCart((prevState) => {
					fixBackgroundScroll(!prevState);
					calculateMenuHeight(prevState ? 0 : window.innerHeight);
					return !prevState;
				});
			}
		}
	};

	const openConsumerSelector = () => {
		deleteCookie('consumer');
		props.changeConsumer();
	};

	const hoverConsumerSelector = (e) => {
		if (e.type === 'mouseenter') {
			updateTradeTitle(`${setup.consumerType === 'private' ? translate(setup, 'consumerTrade') : translate(setup, 'consumerPrivate')}?`);
		} else {
			updateTradeTitle(setup.consumerType === 'private' ? translate(setup, 'consumerPrivate') : translate(setup, 'consumerTrade'));
		}
	};

	useEffect(() => {
		updateTradeTitle(setup.consumerType === 'private' ? translate(setup, 'consumerPrivate') : translate(setup, 'consumerTrade'));
	}, [setup.consumerType]);

	useEffect(() => {
		if (checkout.addedToBag && window.innerWidth <= 600) {
			toggleCart((prevState) => {
				fixBackgroundScroll(!prevState);
				setTimeout(() => {
					fixBackgroundScroll(false);
				}, 4000);
				calculateMenuHeight(prevState ? 0 : window.innerHeight);
				return !prevState;
			});
		}
	}, [checkout.addedToBag]);

	const navigationClasses = [s.navigation];
	if (location.type === 'SEARCH') {
		navigationClasses.push(s.navigation__searchActive);
	}
	if (mobileMenuOpen) navigationClasses.push(s.navigation__menuOpen);
	if (cartOpen) navigationClasses.push(s.navigation__cartOpen);

	const navigationItemsStyle = {
		height: 'auto',
	};
	if (typeof window !== 'undefined' && window.innerWidth <= 600 && mobileMenuOpen) navigationItemsStyle.height = `${menuHeight}px`;

	const cartItemsStyle = {};
	if (typeof window !== 'undefined' && window.innerWidth <= 600) {
		if (cartOpen) cartItemsStyle.height = `${menuHeight}px`;
		else cartItemsStyle.height = '0px';
	}

	return (
		<>
			<nav className={navigationClasses.join(' ')} style={navigationItemsStyle}>
				<a className={s.navigation_mobileMenu} href="#open-mobile-menu" onClick={mobileMenuHandler} aria-label="Open mobile menu"><span /><span /><span /></a>
				<div className={[s.navigation_menu].join(' ')}>
					<div className={s.navigation_menu_overflow}>
						<div className={s.navigation_menu_overflow_items} ref={menuItems}>
							<div className={s.navigation_menu_overflow_items_screen} />
							<div className={s.navigation_menu_overflow_items_background} />
							{
								navigation.header && [...navigation.header, ...navigation.support.slice(0, 1)].map((level1) => (
									<div key={level1.uri} className={[s.navigation_menu_overflow_item, (toggledMenuParent === prefix(setup, translate(setup, level1, 'uri')) ? s.navigation_menu_overflow_item__expanded : null), (level1.subPages ? s.navigation_menu_overflow_item__hasSubItems : null)].join(' ')}>
										<Link className={s.navigation_menu_overflow_item_link} to={prefix(setup, translate(setup, level1, 'uri'))} onClick={navigationParentClicked}>
											<span>{ translate(setup, level1, 'title') }</span>
										</Link>
										{
											level1.subPages && (
												<div className={s.navigation_menu_overflow_item_subMenuItems}>
													{
														level1.subPages.map((level2) => {
															const uri = translate(setup, level2, 'uri');
															if (/(http|mailto|tel)/.test(uri)) {
																return <a className={s.navigation_menu_overflow_item_subMenuItem} href={uri} key={uri} target="_blank" rel="noopener noreferrer nofollow"><span>{ translate(setup, level2, 'title') }</span></a>;
															}
															return <Link key={uri} className={s.navigation_menu_overflow_item_subMenuItem} to={prefix(setup, uri)} onClick={navigationClicked}><span>{ translate(setup, level2, 'title') }</span></Link>;
														})
													}
												</div>
											)
										}
									</div>
								))
							}
						</div>
						{ showHoverClear && <div className={s.navigation_menu_overflow_clear} /> }
					</div>
				</div>
			</nav>
			<Link className={s.logo} to={prefix(setup)} onClick={menuClicked}>
				<svg width="89" height="13" viewBox="0 0 89 13" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M7.29454 12.184C12.0555 12.184 13.5045 11.379 13.5045 8.458V8.228H12.3775V8.412C12.3775 10.551 11.5725 11.149 7.22554 11.149H6.78854C2.02754 11.149 1.19954 10.39 1.19954 6.802V6.549H13.4125V5.33C13.4125 1.696 12.2395 0.775999 7.08754 0.775999H6.60454C1.29154 0.775999 0.0265353 1.65 0.0265353 6.319V6.595C0.0265353 11.172 1.19954 12.184 6.78854 12.184H7.29454ZM1.22254 5.537C1.31454 2.432 1.98154 1.811 6.67354 1.811H7.04154C11.5725 1.811 12.2625 2.455 12.2625 5.169V5.537H1.22254ZM39.1733 5.284C39.1733 1.696 38.0693 0.775999 34.0213 0.775999H33.6073C30.1113 0.775999 28.8693 2.133 28.4093 3.582H28.3863C28.0183 1.673 27.2133 0.775999 23.4183 0.775999H23.0733C19.9453 0.775999 18.6113 1.742 17.9903 3.237H17.9673V0.96H16.7943V12H17.9673V5.859C17.9673 2.708 19.6923 1.811 22.8433 1.811H23.1883C26.5693 1.811 27.3973 2.294 27.3973 5.859V12H28.5703V6.066C28.5703 2.777 30.2263 1.811 33.3773 1.811H33.7683C37.3103 1.811 38.0003 2.432 38.0003 5.882V12H39.1733V5.284ZM49.6109 12.184C54.3719 12.184 55.8209 11.379 55.8209 8.458V8.228H54.6939V8.412C54.6939 10.551 53.8889 11.149 49.5419 11.149H49.1049C44.3439 11.149 43.5159 10.39 43.5159 6.802V6.549H55.7289V5.33C55.7289 1.696 54.5559 0.775999 49.4039 0.775999H48.9209C43.6079 0.775999 42.3429 1.65 42.3429 6.319V6.595C42.3429 11.172 43.5159 12.184 49.1049 12.184H49.6109ZM43.5389 5.537C43.6309 2.432 44.2979 1.811 48.9899 1.811H49.3579C53.8889 1.811 54.5789 2.455 54.5789 5.169V5.537H43.5389ZM65.8957 12.184C70.7487 12.184 72.1517 11.379 72.1517 8.343V8.044H71.0247V8.251C71.0247 10.551 70.1507 11.149 65.8727 11.149H65.3897C60.5367 11.149 59.7777 10.436 59.7777 6.664V6.296C59.7777 2.478 60.4907 1.811 65.3897 1.811H65.8727C70.0587 1.811 70.9327 2.317 70.9327 4.594V4.755H72.0597V4.502C72.0597 1.466 70.8177 0.775999 65.8957 0.775999H65.3667C59.8467 0.775999 58.6047 1.627 58.6047 6.296V6.664C58.6047 11.287 59.8237 12.184 65.3667 12.184H65.8957ZM81.7188 11.149C76.7738 11.149 76.1068 10.574 76.1068 6.664V6.296C76.1068 2.386 76.7508 1.811 81.7188 1.811H82.2018C87.1468 1.811 87.7908 2.386 87.7908 6.296V6.664C87.7908 10.574 87.1468 11.149 82.2018 11.149H81.7188ZM82.2248 12.184C87.9058 12.184 88.9638 11.333 88.9638 6.664V6.296C88.9638 1.604 87.9058 0.775999 82.2248 0.775999H81.6958C76.0148 0.775999 74.9338 1.604 74.9338 6.296V6.664C74.9338 11.333 76.0148 12.184 81.6958 12.184H82.2248Z" fill="black" />
				</svg>
			</Link>
			<div className={s.functions}>
				{
					account.accessToken && (
						<Link className={s.logOut} to={prefix(setup, 'logout')}>
							<span>{ translate(setup, 'accountSignOut') }</span>
						</Link>
					)
				}
				{
					setup.consumerTypeActive && (
						<button className={s.consumer} type="button" onClick={openConsumerSelector} onMouseEnter={hoverConsumerSelector} onMouseLeave={hoverConsumerSelector}>
							<span>{ tradeTitle }</span>
						</button>
					)
				}
				{
					navigation.support && (
						<div className={[s.navigation_menu_overflow_items, s.navigation_menu_overflow_items__right].join(' ')} ref={menuItems}>
							<div className={[s.navigation_menu_overflow_items_screen]} />
							{
								navigation.support.slice(0, 1).map((level1) => (
									<div key={level1.uri} className={[s.information, s.navigation_menu_overflow_item].join(' ')}>
										<Link className={[s.information_link, s.navigation_menu_overflow_item_link].join(' ')} to={prefix(setup, translate(setup, level1, 'uri'))} onClick={navigationParentClicked}>
											<span className={s.information_link_icon}><span /></span>
										</Link>
										{
											level1.subPages && (
												<div className={[s.information_links, s.navigation_menu_overflow_item_subMenuItems].join(' ')}>
													{
														level1.subPages.map((level2) => (
															<Link key={level2.uri} className={s.navigation_menu_overflow_item_subMenuItem} to={prefix(setup, translate(setup, level2, 'uri'))} onClick={navigationClicked}>
																<span>{ translate(setup, level2, 'title') }</span>
															</Link>
														))
													}
												</div>
											)
										}
									</div>
								))
							}
						</div>
					)
				}
				<Link className={s.search} to={prefix(setup, 'search')}>
					<svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
						<circle cx="6.75" cy="6.75" r="6.25" stroke="black" />
						<path d="M11.25 11.25L18 18" stroke="black" />
					</svg>
				</Link>
				<div className={[s.selection, (cartOpen ? s.selection__active : null)].join(' ')}>
					<Link className={s.selection_title} to={setup.consumerType === 'private' ? prefix(setup, 'selection') : prefix(setup, translate(setup, 'checkoutTradeLinkSelection'))} onClick={openCartHandler}>
						<svg width="28" height="18" viewBox="0 0 28 18" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M1 1L5.10526 17H22.8947L27 1" stroke="black" />
						</svg>
						{
							!!totals.totalQuantity && parseInt(totals.totalQuantity, 10) > 0 && (
								<span className={s.selection_title_quantity}>{ totals.totalQuantity }</span>
							)
						}
					</Link>
					<div className={s.selection_cart} style={cartItemsStyle}>
						<Cart position="header" />
					</div>
					<div className={s.selection_cartScreen} />
				</div>
				<div className={s.functions_background} />
			</div>
		</>
	);
};


const mapState = ({ environment, pageStatus, productCache, checkout }, props) => ({
	searchInputText: environment.searchInputText,
	pagePaths: pageStatus.paths,
	productCache,
	checkout,
	...props,
});

export default hot(module)(connect(mapState, { goToPage, searchInput, changeLanguage, changeCountry, changeConsumer })(Navigation));
